import {t} from '../../../common/getTranslations';
import {openWindow} from '../../../common/openWindow.function';
import {formatWithCommas} from '../../../common/shared/utils/formatWithCommas';
import {Maybe, TicketInfo, TicketKind} from '../../../graphql/generated/types';
import {aggregateAndSendLog} from '../../../logging/aggregateAndSendLog';
import {LOG_CLICK_EVENT} from '../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../logging/putTiaraObjectProps';
import {ButtonV2Variant} from '../../atoms/ButtonV2/ButtonV2.enum';
import {Icon} from '../../atoms/Icon/Icon';
import {IconType} from '../../atoms/Icon/IconType.enum';
import {Text} from '../../atoms/Text/Text';
import {Dialog} from '../Dialog';
import {DialogButtonProps} from '../Dialog/DialogButton';

export interface SetBuySelectDialogProps {
  isMobile: boolean;
  setId: number;
  seriesId: number;
  title?: string;
  hasRentalProduct?: boolean;
  hasOwnProduct?: boolean;
  cashAmount?: Maybe<number>;
  purchasableTicketRental?: Maybe<TicketInfo>;
  purchasableTicketOwn?: Maybe<TicketInfo>;
  onBuySelect: (ticketInfo: TicketInfo) => void;
  onClickCancel: () => void;
}

export function SetBuySelectDialog({
  isMobile,
  seriesId,
  title,
  cashAmount,
  hasRentalProduct,
  hasOwnProduct,
  purchasableTicketRental,
  purchasableTicketOwn,
  onBuySelect,
  onClickCancel,
}: SetBuySelectDialogProps) {
  return (
    <Dialog
      data-test="set-buy-select-dialog"
      isOpen={true}
      onClose={onClickCancel}
      isUsingScrollBlock={false}
      isMobile={isMobile}
      {...putTiaraObjectProps({
        click: {
          layer1: '세트구매팝업',
          imp_id: 'popup_p_set',
          imp_provider: 'service',
        },
        viewimp_contents: {
          layer1: '세트구매팝업',
          imp_id: 'popup_p_set',
          imp_provider: 'service',
          imp_type: 'vimp',
        },
        customProps: {
          popup_type: '세트구매팝업',
          page_ticket_cnt: '1',
        },
      })}
      onInView={target => {
        aggregateAndSendLog({
          targetElem: target as HTMLElement,
          actionType: 'ViewImp',
          actionName: '팝업_노출',
          customProps: {
            user_action: 'imp',
          },
        });
      }}
    >
      <Dialog.Dimmed />
      <Dialog.Title>{title}</Dialog.Title>
      <Dialog.List>
        {purchasableTicketRental && hasRentalProduct ? (
          <Dialog.Label className="text-static-point-red font-small1">
            {t('purchase_cash_popup_info2')}
          </Dialog.Label>
        ) : hasOwnProduct ? (
          <Dialog.Label className="text-static-point-red">
            {t('purchase_cash_popup_info3')}
          </Dialog.Label>
        ) : null}
        <Dialog.Divider />
      </Dialog.List>
      <Dialog.List>
        <div className="flex flex-row justify-between">
          <Dialog.Label className="text-theme-solid-100 font-small1">
            {t('sethome_set_buy_popup_cash_owned', {
              values: {
                value: formatWithCommas(cashAmount || 0),
              },
            })}
          </Dialog.Label>
          <div className="flex space-x-4px">
            <Dialog.Label className="font-small1 text-theme-solid-60">
              {t('sethome_set_buy_popup_title')}
            </Dialog.Label>
            <Icon
              type={IconType.ContentHomeHelpSolid}
              className="cursor-pointer"
              onClick={e =>
                LOG_CLICK_EVENT('콘텐츠구매안내_클릭', e, () => {
                  openWindow({
                    url: `https://${window.location.host}/content/policy/cash?regulationType=set`,
                    target: '콘텐츠 구매',
                    features: 'width=360,height=800',
                  });
                })
              }
            />
          </div>
        </div>
      </Dialog.List>
      <Dialog.List className="px-18pxr pb-10pxr">
        {purchasableTicketRental && !!purchasableTicketRental.ticketId && (
          <Dialog.Button
            data-test="ticket-rent-button"
            {...getBuyButtonProps({
              ticketKind: TicketKind.Rent,
              ticketInfo: purchasableTicketRental,
            })}
          />
        )}
        {purchasableTicketOwn && !!purchasableTicketOwn.ticketId && (
          <Dialog.Button
            data-test="ticket-buy-button"
            {...getBuyButtonProps({
              ticketKind: TicketKind.Own,
              ticketInfo: purchasableTicketOwn,
            })}
          />
        )}
      </Dialog.List>
      <Dialog.List className="pb-0">
        <Dialog.Caption>{t('sethome_set_buy_popup_info')}</Dialog.Caption>
      </Dialog.List>
      <Dialog.BottomCombination>
        <Dialog.Button
          onClick={e => LOG_CLICK_EVENT('취소_클릭', e, onClickCancel)}
        >
          {t('common_cancel_button')}
        </Dialog.Button>
      </Dialog.BottomCombination>
    </Dialog>
  );
  function getBuyButtonProps({
    ticketKind,
    ticketInfo,
  }: {
    ticketKind: TicketKind;
    ticketInfo: TicketInfo;
  }): DialogButtonProps {
    return {
      description: (
        <Text>
          {ticketInfo?.discountPrice && (
            <>
              <Text className="text-theme-solid-60 line-through">
                {formatWithCommas(ticketInfo.price)}
              </Text>
              &nbsp;
            </>
          )}
          <Text>
            {t('sethome_set_buy_popup_price', {
              values: {
                value: formatWithCommas(
                  ticketInfo.discountPrice ?? ticketInfo.price
                ),
              },
            })}
          </Text>
        </Text>
      ),
      ...putTiaraObjectProps({
        click: {
          copy: `${ticketKind === TicketKind.Own ? '소장' : '대여'}_${
            ticketInfo.discountPrice ?? ticketInfo.price
          }캐시`,
        },
        customProps: {
          event_series_id: String(seriesId),
          event_ticket_type: `${
            ticketKind === TicketKind.Own ? '소장' : '대여'
          }_${ticketInfo.discountPrice ?? ticketInfo.price}캐시`,
        },
      }),
      onClick: e =>
        LOG_CLICK_EVENT(
          `${ticketKind === TicketKind.Own ? '소장' : '대여'}_클릭`,
          e,
          () => onBuySelect(ticketInfo)
        ),
      children:
        ticketKind === TicketKind.Own
          ? t('common_content_possession')
          : t('purchase_cash_popup_rent', {
              values: {
                value: t('common_period_day', {
                  values: {
                    value: formatWithCommas(
                      (purchasableTicketRental?.rentalPeriodByMinute ?? 0) /
                        (60 * 24)
                    ),
                  },
                }),
              },
            }),
      variant: ButtonV2Variant.Primary,
    };
  }
}
